import React from 'react';
import styled from 'styled-components';
import ContainerDimensions from 'react-container-dimensions';
import { number, bool, shape } from 'prop-types';

/**
 * Position tooltip next to target point
 * so it's not clamped by the edges of the container
 *
 * @param {number} x - Target point x
 * @param {number} y - Target point y
 * @param {number} width - width of the tooltip
 * @param {number} height - height of the tooltip
 * @param {{ x: number, y: number }} padding - Minimum space between tooltip and target
 * @param {{ top: number, right: number, bottom: number, left: number }} container - container bounds
 */
const positionTooltip = (x, y, width, height, padding, container) => {
  // x, y - target point
  // defaultX, defaultY - position tooltip above and to the right of the target
  const defaultX = x + padding.x;
  const defaultY = y - height - padding.y;

  // Top left corner Y after we handle edge cases of positioning
  const correctedY =
    // If tooltip is clamped by the top edge of the container
    defaultY < container.top
      ? // Place tooltip below the target point or right below the top edge of the container
        // if target point is way above
        Math.max(container.top + padding.y, y + padding.y)
      : // If tooltip is clamped by the bottom edge of the container
        y > container.bottom
        ? // Place it right above the bottom edge
          container.bottom - height - padding.y
        : // if the tooltip isn't clamped by the container, use default positioning
          defaultY;

  // Top left corner X after we handle edge cases of positioning
  const correctedX =
    // If tooltip is clamped by container's right edge
    defaultX + width + padding.x > container.right
      ? // Position tooltip to the left of target point or on the right edge of the container
        // if target point is too far to the right
        Math.min(x - width - padding.x, container.right - width - padding.x) >
        container.left
        ? Math.min(x - width - padding.x, container.right - width - padding.x)
        : container.left - padding.x
      : // If tooltip is clamped by the left edge of the container
        x < container.left
        ? // Place it right next to the left edge of the container
          container.left + padding.x
        : // if the tooltip isn't clamped by the container, use default positioning
          defaultX;

  return `translate(${Math.round(correctedX)}px, ${Math.round(correctedY)}px)`;
};

const Container = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 20; // XXX: because selected point on map scatter is 10
`;

export const TooltipPositioner = ({
  x,
  y,
  padding,
  container,
  hoverable,
  children,
}) => (
  <Container style={{ pointerEvents: hoverable ? 'auto' : 'none' }}>
    <ContainerDimensions>
      {({ width, height }) => (
        <div
          style={{
            visibility: width === 0 ? 'hidden' : 'visible',
            transform: positionTooltip(x, y, width, height, padding, container),
          }}
        >
          {children}
        </div>
      )}
    </ContainerDimensions>
  </Container>
);

TooltipPositioner.propTypes = {
  x: number.isRequired,
  y: number.isRequired,
  container: shape({ top: number, right: number, bottom: number, left: number })
    .isRequired,
  padding: shape({ y: number, x: number }),
  hoverable: bool,
};

TooltipPositioner.defaultProps = {
  padding: { y: 0, x: 0 },
};

export default TooltipPositioner;



// WEBPACK FOOTER //
// ./src/components/GenericTooltip/TooltipPositioner.jsx